import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Instagram, Facebook, Mail, Check } from "lucide-react";

const Threads = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 192 192" fill="currentColor" aria-hidden="true"><path d="M141.5 88.9c-.8-.4-1.7-.8-2.5-1.2-1.5-27.1-16.3-42.6-41.1-42.8h-.3c-14.8 0-27.2 6.3-34.8 17.9l13.6 9.3c5.7-8.6 14.6-10.4 21.2-10.4h.2c8.2.1 14.4 2.4 18.4 7.1 2.9 3.4 4.9 8.1 5.8 14.1-7.3-1.2-15.1-1.6-23.5-1.1-23.6 1.4-38.8 15.1-37.8 34.2.5 9.7 5.3 18 13.6 23.5 7 4.6 16 6.9 25.3 6.4 12.3-.7 22-5.4 28.7-14 5.1-6.5 8.4-15 9.8-25.6 5.8 3.5 10.1 8.1 12.5 13.7 4.1 9.5 4.3 25.1-8.4 37.8-11.2 11.1-24.6 15.9-44.9 16.1-22.5-.2-39.5-7.4-50.6-21.4C36.3 140.3 31 121.4 30.8 96c.2-25.4 5.5-44.3 15.9-57.2C57.8 24.8 74.8 17.6 97.3 17.4c22.7.2 40 7.4 51.5 21.5 5.6 6.9 9.9 15.6 12.7 25.7l15.9-4.2c-3.4-12.6-8.8-23.5-16.2-32.6C146.2 9.8 125 .6 97.4.4h-.1C69.7.6 48.8 9.8 34.8 27.5 22.3 43.3 15.9 65.2 15.7 96v.1c.2 30.8 6.6 52.7 19.1 68.5 14 17.7 34.9 26.9 62.4 27.1h.1c24.5-.2 41.7-6.6 55.9-20.8 18.6-18.6 18-41.9 11.9-56.2-4.4-10.2-12.8-18.5-23.6-23.8zm-42.4 39.8c-10.3.6-21-4-21.5-13.9-.4-7.3 5.2-15.5 22.1-16.5 1.9-.1 3.8-.2 5.7-.2 6.1 0 11.9.6 17.1 1.7-1.9 24.2-13.3 28.3-23.4 28.9z"/></svg>
);

export default function Footer() {
  const { settings } = useSettings();
  const menus = (settings?.footer_menus || []).filter((m) => m.enabled);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/newsletter/subscribe", { email }); setDone(true); setEmail(""); }
    catch (err) {} finally { setBusy(false); }
  };

  return (
    <footer className="mt-24 border-t border-white/10 bg-[hsl(var(--card))]/40">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-12">
        <div className="card-surface rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 mb-10">
          <div>
            <h3 className="text-xl font-bold text-white">Join the newsletter</h3>
            <p className="text-sm text-white/50 mt-1">Occasional updates on esports, tournaments and the biggest gaming stories. No spam.</p>
          </div>
          {done ? (
            <div className="flex items-center gap-2 text-green-400 font-semibold" data-testid="newsletter-done"><Check size={18} /> You're subscribed — check your inbox!</div>
          ) : (
            <form onSubmit={subscribe} className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="newsletter-email"
                  placeholder="you@example.com" className="w-full pl-10 pr-3 py-2.5 rounded-full bg-black/40 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]" />
              </div>
              <button type="submit" disabled={busy} data-testid="newsletter-submit" className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold disabled:opacity-60">Subscribe</button>
            </form>
          )}
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Logo />
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            {menus.map((m) => (
              <Link key={m.id} to={m.path} className="text-sm font-semibold text-white/60 hover:text-white transition-colors">{m.label}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-3 text-white/50">
            {[["instagram", Instagram], ["facebook", Facebook], ["threads", Threads]].map(([k, Icon]) => (
              <a key={k} href={settings?.social?.[k] || "#"} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`social-${k}`} className="p-2 rounded-full border border-white/10 hover:text-white hover:border-white/30 transition-colors"><Icon size={18} /></a>
            ))}
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>© 2026 {settings?.site_name || "7 Legendary"}. All rights reserved.</span>
          <div className="flex gap-5">
            <Link to="/about" className="hover:text-white" data-testid="footer-about">About</Link>
            <Link to="/help" className="hover:text-white" data-testid="footer-help">Help</Link>
            <Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact</Link>
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
